<template>
  <b-card id="page-Azure" no-body class="mb-0 p-2">
    <b-card-header
      class="pl-0 pr-0 d-flex align-items-between justify-content-between"
    >
      <b-col cols="4" sm="4" md="4" lg="4" xl="4">
        <b-card-title
          ><h2>{{ $t(label("title")) }}</h2></b-card-title
        >
      </b-col>
      <b-col
        cols="8"
        sm="8"
        md="8"
        lg="8"
        xl="8"
        class="d-flex justify-content-end align-items-end pt-2"
      >
        <b-button
          id="tooltip-create"
          variant="primary"
          @click="onCreate"
          class="btn-icon"
        >
          <feather-icon icon="PlusIcon" size="18" />
          <span class="text-nowrap">{{ $t(label("register")) }}</span>
        </b-button>
      </b-col>
      <b-tooltip
        placement="top"
        target="tooltip-create"
        triggers="hover"
        @click="onInfo"
      >
        <strong>
          <feather-icon icon="ExternalLinkIcon" class="mr-25 mt-0" />
          Ver tutorial
        </strong>
        <span> </span>
      </b-tooltip>
    </b-card-header>

    <b-table
      id="Azure-table"
      responsive
      :head-variant="headVariant"
      :striped="striped"
      :bordered="bordered"
      :borderless="borderless"
      :outlined="outlined"
      :per-page="perPage"
      :busy="loading"
      :items="items"
      :fields="fields"
      class="d-none d-md-table shadow-sm !rounded"
    >
      <template #head()="data">
        <span class="text-black">{{
          $t(labelHeader(data.label.toLowerCase()))
        }}</span>
      </template>
      <template #cell(nameCompany)="data">
        <slot name="cell-nameCompany" :data="data">
          {{ data.item.nameCompany || "N/A" }}
        </slot>
      </template>

      <template #cell(urlBot)="data">
        <slot name="cell-urlBot" :data="data">
          <b-badge pill :variant="'light-success'" @click="info(data.value, 1)">
            <feather-icon class="text-success" size="18" icon="Link2Icon" />
          </b-badge>

          <b-button
            class="btn-icon"
            v-ripple.400="'rgba(40, 199, 111, 0.15)'"
            variant="bg-info bg-darken-2"
            v-clipboard:copy="data.value"
            v-clipboard:success="onCopy"
            v-clipboard:error="onError"
          >
            <feather-icon icon="CopyIcon" />
          </b-button>
        </slot>
      </template>
      <template #cell(pathPlainText)="data">
        <slot name="cell-pathPlainText" :data="data">
          {{ data.item.pathPlainText || "N/A" }}
        </slot>
      </template>
      <template #cell(pathImg)="data">
        <slot name="cell-pathImg" :data="data">
          {{ data.item.pathImg || "N/A" }}
        </slot>
      </template>
      <template #cell(pathVoice)="data">
        <slot name="cell-pathVoice" :data="data">
          {{ data.item.pathVoice || "N/A" }}
        </slot>
      </template>
      <template #cell(pathStartBot)="data">
        <slot name="cell-pathStartBot" :data="data">
          {{ data.item.pathStartBot || "N/A" }}
        </slot>
      </template>
      <template #cell(pathStopBot)="data">
        <slot name="cell-pathStopBot" :data="data">
          {{ data.item.pathStopBot || "N/A" }}
        </slot>
      </template>
      <template #cell(apiKey)="data">
        <slot name="cell-apiKey" :data="data">
          <b-badge
            pill
            :variant="'light-success'"
            @click="info(data.value, 2)"
            style="margin-right: 3px"
          >
            <feather-icon class="text-success" size="18" icon="KeyIcon" />
          </b-badge>

          <b-button
            class="btn-icon"
            v-ripple.400="'rgba(40, 199, 111, 0.15)'"
            variant="bg-info bg-darken-2"
            v-clipboard:copy="data.value"
            v-clipboard:success="onCopy"
            v-clipboard:error="onError"
          >
            <feather-icon icon="CopyIcon" />
          </b-button>
        </slot>
      </template>
      <template #cell(actions)="data">
        <slot name="cell-actions" :data="data">
          <feather-icon
            class="text-success mr-25"
            size="18"
            icon="FileTextIcon"
            @click="onShow(data.item.idArquitecturaAzure)"
            aria-label="Show details"
          />
          <feather-icon
            class="text-primary mr-25"
            size="18"
            icon="EditIcon"
            @click="onEdit(data.item.idArquitecturaAzure)"
            aria-label="edit details"
          />
          <feather-icon
            class="text-danger"
            size="18"
            icon="Trash2Icon"
            @click="onDelete(data.item.idArquitecturaAzure)"
            aria-label="Delete details"
          />
        </slot>
      </template>
      <template #table-busy>
        <div class="text-center text-primary my-2">
          <b-spinner class="align-middle ml-50"></b-spinner>
          <strong>{{ $t("loading") }}...</strong>
        </div>
      </template>
    </b-table>

    <!-- Pagination -->
    <div class="mx-2 d-none d-md-table">
      <b-row>
        <b-col cols="12" md="2" class="mb-2">
          <b-form-group class="mb-0">
            <label class="d-inline-block text-sm-left mr-50">
              {{ $t("perPage") }}
            </label>
            <b-form-select
              id="perPageSelect"
              v-model="perPage"
              size="sm"
              :options="pageOptions"
              @change="handlePerPageChange"
              class="w-50"
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="12"
          md="10"
          class="d-flex align-items-center justify-content-end"
        >
          <b-pagination
            v-model="currentPage"
            :total-rows="totalRows"
            :per-page="perPage"
            pills
            @change="handlePageChange"
            first-number
            last-number
          >
            <template #prev-text>
              <feather-icon size="18" icon="ChevronLeftIcon" />
            </template>
            <template #next-text>
              <feather-icon size="18" icon="ChevronRightIcon" />
            </template>
          </b-pagination>
        </b-col>
      </b-row>
    </div>

    <!-- List for mobile devices -->
    <div id="list-container" class="d-block d-md-none mt-1 list-container">
      <b-list-group>
        <b-list-group-item
          v-for="item in items"
          :key="item.guid"
          class="d-flex flex-column align-items-start border border-primary rounded mb-1"
        >
          <div class="d-flex w-100 justify-content-between mb-1">
            <strong> {{ item.nameCompany || "N/A" }}</strong>
            <b-dropdown
              variant="link"
              no-caret
              class="chart-dropdown"
              toggle-class="p-0"
              right
            >
              <template #button-content>
                <feather-icon
                  icon="MoreVerticalIcon"
                  size="18"
                  class="text-body cursor-pointer"
                />
              </template>
              <b-dropdown-item @click="onShow(item.guid)">
                <div class="d-flex align-items-center justify-content-between">
                  <feather-icon
                    class="text-success mr-25"
                    size="18"
                    icon="FileTextIcon"
                  />
                  {{ $t("show") }}
                </div>
              </b-dropdown-item>
              <b-dropdown-item @click="onEdit(item.guid)">
                <div class="d-flex align-items-center justify-content-between">
                  <feather-icon
                    class="text-primary mr-25"
                    size="18"
                    icon="EditIcon"
                  />
                  {{ $t("edit") }}
                </div>
              </b-dropdown-item>
              <b-dropdown-item @click="onDelete(item.guid)">
                <div class="d-flex align-items-center justify-content-between">
                  <feather-icon
                    class="text-danger"
                    size="18"
                    icon="Trash2Icon"
                  />
                  {{ $t("delete") }}
                </div>
              </b-dropdown-item>
            </b-dropdown>
          </div>
          <!-- <p class="m-0">
            <strong>{{ $t(labelHeader("nameCompany")) }}: </strong>
            {{ item.nameCompany || "N/A" }}
          </p> -->
          <p class="m-0">
            <strong>{{ $t(labelHeader("url_bot")) }}: </strong
            >{{ item.urlBot || "N/A" }}
          </p>
        </b-list-group-item>
        <b-list-group-item v-if="items.length == 0" class="text-center">
          {{ $t("notRegister") }}
        </b-list-group-item>
        <b-list-group-item v-if="loading" class="text-center">
          <b-spinner class="align-middle ml-50" variant="primary"></b-spinner>
        </b-list-group-item>
      </b-list-group>
    </div>
  </b-card>
</template>
<script>
import _ from "lodash";
import {
  BCard,
  BCardHeader,
  BCardTitle,
  BRow,
  BCol,
  BButton,
  BTable,
  BPagination,
  BToast,
  BSpinner,
  BBadge,
  VBTooltip,
  BTooltip,
  BFormSelect,
  BFormGroup,
  BListGroup,
  BListGroupItem,
  BDropdown,
  BDropdownItem,
} from "bootstrap-vue";
import Ripple from "vue-ripple-directive";
import {
  deleteAzureArqu,
  getAllAzureArqu,
} from "@core/api/request/azureArquitecture";
import { getParams, showLoading, stopLoading } from "@core/utils/utils";

// import Filterss from "../components/filters/Filterss.vue";

export default {
  components: {
    BCard,
    BCardHeader,
    BCardTitle,
    BRow,
    BCol,
    BButton,
    BTable,
    BPagination,
    BToast,
    BSpinner,
    BBadge,
    VBTooltip,
    BTooltip,
    BFormSelect,
    BFormGroup,
    BListGroup,
    BListGroupItem,
    BDropdown,
    BDropdownItem,
  },
  directives: {
    "b-tooltip": BTooltip,
    Ripple,
  },
  data: () => ({
    companyGuid: null,
    loading: false,
    totalRows: null,
    currentPage: 1,
    perPage: 10,
    pageOptions: [10, 50, 100],
    headVariant: "light",
    striped: false,
    outlined: false,
    bordered: true,
    borderless: false,
    hasNextPage: true,

    items: [],
    fields: [
      { label: "company", key: "nameCompany", sortable: false },
      { label: "url_bot", key: "urlBot", sortable: true },
      { label: "path_plain_text", key: "pathPlainText", sortable: true },
      { label: "path_img", key: "pathImg", sortable: false },
      { label: "path_voice", key: "pathVoice", sortable: false },
      { label: "path_starbot", key: "pathStartBot", sortable: false },
      { label: "path_stopbot", key: "pathStopBot", sortable: false },
      { label: "api_key", key: "apiKey", sortable: true },
      { label: "action", key: "actions", sortable: false },
    ],
  }),
  watch: {
    "$store.state.appConfig.activeCompanyGuid"(guid) {
      this.companyGuid = guid;
      this.items = [];
      this.hasNextPage = true;
      this.currentPage = 1;
    },
  },

  async mounted() {
    const listContainer = document.getElementById("list-container");
    if (listContainer) {
      listContainer.addEventListener("scroll", this.handleScroll);
    }
  },
  beforeDestroy() {
    const listContainer = document.getElementById("list-container");
    if (listContainer) {
      listContainer.removeEventListener("scroll", this.handleScroll);
    }
  },

  created() {
    this.companyGuid = this.$store.state.appConfig.activeCompanyGuid;
    this.loadData();
  },

  methods: {
    async loadData() {
      try {
        this.loading = true;
        const params = this.chargeParams();

        // Usamos Promise.all para cargar productos y catálogos en paralelo
        const [containersResp] = await Promise.all([getAllAzureArqu(params)]);

        // Procesamos productos
        const containers = _.get(containersResp, "data");
        this.items = containers?.items || [];
        this.totalRows = containers?.totalRows || 0;
        this.hasNextPage =
          containers?.currentPage < containers?.totalPages || 0;
      } catch (error) {
        this.alertToast("danger", "Error", error.message, "AlertTriangleIcon");
        console.error("Error loading data", error.message);
      } finally {
        this.loading = false;
      }
    },
    loadDataInfinity() {
      if (!this.hasNextPage) return;
      try {
        this.loading = true;
        const params = this.chargeParams();
        getAllAzureArqu(params)
          .then((resp) => {
            const data = _.get(resp, "data", []);
            if (Array.isArray(data?.items)) {
              // Agregar nuevos elementos sin sobreescribir los existentes
              this.items = [...this.items, ...data?.items];
            }
            this.totalRows = data?.totalRows || 0;
            // Verificar si hay más páginas
            this.hasNextPage = data?.currentPage < data?.totalPages || false;
            this.loading = false;
          })
          .catch((error) => {
            this.alertToast(
              "danger",
              "Error",
              error.message,
              "AlertTriangleIcon",
            );
            console.error("Error loading products infinitely", error);
          });
      } catch (error) {
        this.alertToast("danger", "Error", error.message, "AlertTriangleIcon");
        console.error("Error cargando productos infinitamente", error.message);
      }
    },
    label(field) {
      return `pages.azure.table.${field}`;
    },

    labelHeader(field) {
      return `pages.azure.table.tableHeader.${field}`;
    },

    chargeParams() {
      const arrParams = [];

      arrParams.push(`page,${this.currentPage}`);
      arrParams.push(`rows,${this.perPage}`);

      return getParams(arrParams);
    },
    handleScroll() {
      const container = document.getElementById("list-container");
      if (
        container.scrollTop + container.clientHeight >=
        container.scrollHeight - 1
      ) {
        this.currentPage++;
        this.loadDataInfinity();
      }
    },

    handlePageChange(value) {
      this.currentPage = value;
      this.hasNextPage = true;
      this.loadData();
    },

    handlePerPageChange() {
      this.loadData();
    },
    info(guid, i) {
      this.$swal({
        title: i == 1 ? "Url del bot" : "Llave de api",
        text: guid,
        icon: "info",
        customClass: {
          confirmButton: "btn btn-primary",
        },
        buttonsStyling: false,
      });
    },

    onCopy() {
      this.alertToast(
        "success",
        "Texto copiado!",
        "Texto copiado al portapapeles!",
        "CheckIcon",
      );
    },
    onCreate() {
      this.$router.push({
        name: "azureArchitectureForm",
        params: { option: 1 },
      });
    },

    onShow(idArquitecturaAzure) {
      this.$router.push({
        name: "azureArchitectureForm",
        params: {
          option: 2,
          idArquitecturaAzure: idArquitecturaAzure,
        },
      });
    },

    onEdit(idArquitecturaAzure) {
      this.$router.push({
        name: "azureArchitectureForm",
        params: {
          option: 3,
          idArquitecturaAzure: idArquitecturaAzure,
        },
      });
    },

    onDelete(idArquitecturaAzure) {
      this.$swal({
        title: this.$t("areYouSure"),
        text: this.$t("willNotBeAble"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: this.$t("yesDelete"),
        customClass: {
          confirmButton: "btn btn-primary",
          cancelButton: "btn btn-outline-danger ml-1",
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.value) {
          showLoading();
          deleteAzureArqu(idArquitecturaAzure)
            .then(() => {
              stopLoading();
              this.$swal({
                icon: "success",
                title: this.$t("Deleted"),
                text: this.$t("beenDeleted"),
                customClass: {
                  confirmButton: "btn btn-success",
                },
              });
              this.loadData();
            })
            .catch((error) => {
              console.error("error", error);
              stopLoading();
              this.alertToast(
                "danger",
                this.$t("codeAlert"),
                error.message,
                "AlertTriangleIcon",
              );
            });
        } else if (result.dismiss === "cancel") {
          this.message(
            this.$t("canceled"),
            this.$t("registerDeletedCanceled"),
            "error",
          );
        }
      });
    },

    onInfo() {
      console.log("Displaying tutorial info");
    },

    message(title, text, icon, html) {
      this.$swal({
        title,
        text,
        html,
        icon,
        customClass: {
          confirmButton: "btn btn-success",
        },
      });
    },

    alertToast(type = null, title = null, message = null, icon) {
      this.$bvToast.toast(message, {
        title,
        variant: type,
        solid: true,
        icon,
      });
    },
  },
};
</script>
<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}
.list-container {
  height: 60vh !important; /* Ajusta el alto según tus necesidades */
  overflow-y: auto;
}
</style>
